import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  RefObject,
} from 'react';
import {
  useMutation,
} from '@tanstack/react-query';
import {
  getApiStickyGetAllStickiesByPatientId,
  getApiStickyGetStickyById,
  postApiStickyCreateSticky,
  postApiStickyUpdateSticky,
  deleteApiStickyDeleteSticky,
} from 'endpoints';
import {
  normalizeContentForTextarea,
} from 'views/EMR/Settings/helper';
import useStickyNotesStore, {
  useStickyActions,
} from 'views/EMR/Sticky/stores/stickyNotesStore';
import {
  StickyApi,
  StickyDraft,
} from 'views/EMR/Sticky/types';
import isNil from 'lodash/isNil';
import {
  useConfirmDialog,
} from 'core/hooks';
import {
  CreateStickyRequestDTO,
  DeleteStickyRequestDTO,
  UpdateStickyRequestDTO,
} from 'dtos';

type StickyEditingFocusActions = {
  setEditingStickyUniqueId: (uniqueId: string | null) => void;
};

type StickyEditingFocusResult = {
  handleInputFocus: () => void;
  handleInputBlur: () => void;
};

export function useStickyEditingFocus(
  uniqueId: string | undefined,
  actions: StickyEditingFocusActions,
): StickyEditingFocusResult {
  const focusCount = useRef(0);
  const { setEditingStickyUniqueId } = actions;

  const handleInputFocus = useCallback(() => {
    focusCount.current += 1;
    if (uniqueId) {
      setEditingStickyUniqueId(uniqueId);
    }
  }, [uniqueId, setEditingStickyUniqueId]);

  const handleInputBlur = useCallback(() => {
    focusCount.current -= 1;
    setTimeout(() => {
      const currentEditingUniqueId = useStickyNotesStore.getState().editingStickyUniqueId;
      if (focusCount.current === 0 && currentEditingUniqueId === uniqueId) {
        setEditingStickyUniqueId(null);
      }
    }, 0);
  }, [uniqueId, setEditingStickyUniqueId]);

  return { handleInputFocus, handleInputBlur };
}

export function useStickyApi(): StickyApi {
  const addMutation = useMutation({
    mutationFn: (data: CreateStickyRequestDTO) => postApiStickyCreateSticky(data),
  });

  const editMutation = useMutation({
    mutationFn: (data: UpdateStickyRequestDTO) => postApiStickyUpdateSticky(data),
  });

  const deleteMutation = useMutation({
    mutationFn: (data: DeleteStickyRequestDTO) => deleteApiStickyDeleteSticky(data),
  });

  return useMemo(
    () => ({
      fetchStickies: async (patientId: number) => {
        const result = await getApiStickyGetAllStickiesByPatientId({ patientId });
        return result.map((sticky) => ({
          ...sticky,
          content: normalizeContentForTextarea(sticky?.content ?? ''),
        }));
      },
      fetchSticky: async (_patientId: number, stickyId: number) => {
        const result = await getApiStickyGetStickyById({ stickyId });
        return {
          ...result,
          content: normalizeContentForTextarea(result?.content ?? ''),
        };
      },
      addSticky: async (
        patientId: number,
        payload: StickyDraft,
        clientId: string,
      ) => addMutation.mutateAsync({
        patientId,
        ...payload,
        clientId,
      }),
      editSticky: async (
        _patientId: number,
        stickyId: number,
        payload: StickyDraft,
        clientId: string,
      ) => editMutation.mutateAsync({
        stickyId,
        ...payload,
        clientId,
      }),
      deleteSticky: async (
        _patientId: number,
        stickyId: number,
        clientId: string,
      ) => {
        await deleteMutation.mutateAsync({
          stickyId,
          clientId,
        });
      },
    }),
    [addMutation, editMutation, deleteMutation],
  );
}

export function useFlashVisible(flashUntil: number | undefined): boolean {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    if (!flashUntil) return undefined;

    const delay = flashUntil - Date.now();
    if (delay <= 0) return undefined;

    const t = setTimeout(() => setNow(Date.now()), delay);
    return () => clearTimeout(t);
  }, [flashUntil]);

  return !!flashUntil && flashUntil > now;
}

type ContainerFocusProps = {
  containerRef: RefObject<HTMLDivElement | null>;
  onFocusIn?: () => void;
  onFocusOut?: () => void;
};

export function useContainerFocus({
  containerRef,
  onFocusIn,
  onFocusOut,
}: ContainerFocusProps): boolean {
  const [isInteracting, setIsInteracting] = useState(false);

  useEffect(() => {
    const container = containerRef.current;

    function handleFocusIn() {
      if (!isNil(container)) {
        setIsInteracting(true);
        onFocusIn?.();
      }
    }

    function handleFocusOut(e: FocusEvent) {
      if (!isNil(container)) {
        const relatedTarget = e.relatedTarget as Node | null;
        if (!relatedTarget || !container?.contains(relatedTarget)) {
          setIsInteracting(false);
          onFocusOut?.();
        }
      }
    }

    container?.addEventListener('focusin', handleFocusIn);
    container?.addEventListener('focusout', handleFocusOut);

    return () => {
      container?.removeEventListener('focusin', handleFocusIn);
      container?.removeEventListener('focusout', handleFocusOut);
    };
  }, [onFocusIn, onFocusOut]);

  return isInteracting;
}

export function useConfirmDeleteSticky(stickyId: number, isNew: boolean) {
  const { deleteSticky } = useStickyActions();
  const { confirm, closeConfirm } = useConfirmDialog();

  const handleDelete = useCallback(async () => {
    if (isNew) {
      await deleteSticky(stickyId);
    } else {
      confirm({
        title: 'Delete sticky',
        content: (
          <p>
            Are you sure you would like to delete the sticky note?
            ?
          </p>
        ),
        onConfirm: async () => {
          try {
            await deleteSticky(stickyId);
            closeConfirm();
          } catch (error) {
            console.error('Error deleting sticky', error);
          }
        },
      });
    }
  }, [stickyId, isNew]);

  return handleDelete;
}
